/**
 * RBAC custom roles — Phase 1 shapes.
 * Plan-RBAC-SchemaRoles-2026-08.md §4 (data model), §6 (frontend).
 *
 * 🚨 The schema LIST is not declared here. It comes from
 * `GET /api/roles/schemas` at runtime, because a schema whose feature flags are
 * all off is OMITTED from that response entirely — absence IS the hiding
 * mechanism. Hardcoding 16 keys in the frontend would resurrect the dark
 * feature in the permissions UI even while the page itself stays gated
 * (`feedback_dark_ship_means_gating_emission_too`). The `NavSchemaKey` union
 * below is only the nav-item mapping, not a catalogue.
 */

/** The four verbs. `view` is the baseline — without it read-only is inexpressible. */
export type Verb = 'view' | 'add' | 'edit' | 'delete';

export const VERBS: Verb[] = ['view', 'add', 'edit', 'delete'];

/** Verbs that imply `view`: you cannot act on what you cannot see. */
export const WRITE_VERBS: Verb[] = ['add', 'edit', 'delete'];

export interface VerbSet {
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
}

/**
 * Per-schema verb grants. 🚨 An ABSENT key means all verbs false — the backend
 * emits omit-never-zero, so a role doc never carries an all-false schema. Read
 * through `getVerbSet()` rather than indexing directly.
 */
export type RolePermissions = Record<string, Partial<VerbSet> | undefined>;

/** One row of the matrix, as returned by `GET /api/roles/schemas`. */
export interface SchemaDescriptor {
  key: string;
  label: string;
  /** Schema is behind a dark-ship feature flag (informational). */
  flagged?: boolean;
  /**
   * Verbs that can never be granted to a custom role, whatever the matrix says.
   * `domain_identities` returns `["add","delete"]` — DNS records, SPF/DKIM and
   * public Help Center URLs leave the product, so those stay admin/owner-only
   * (owner decision, plan §11.2). Rendered disabled WITH a reason, never
   * silently missing.
   */
  locked_verbs?: Verb[];
}

export interface RoleWithPermissions {
  id: string;
  name: string;
  description: string;
  /** Preset role — immutable, undeletable. The 4 legacy roles. */
  system: boolean;
  /** null = global preset; a workspace id = custom role scoped to it. */
  workspace_id: string | null;
  permissions: RolePermissions;
}

export const EMPTY_VERB_SET: VerbSet = {
  view: false, add: false, edit: false, delete: false,
};

/** Safe read of a schema's verbs — absent key resolves to all-false. */
export const getVerbSet = (
  permissions: RolePermissions | undefined,
  schema: string,
): VerbSet => ({ ...EMPTY_VERB_SET, ...(permissions?.[schema] ?? {}) });

/**
 * Re-derive `view` from the write verbs.
 *
 * 🚨 Plan §4: "enforce on write in the role editor, and re-derive on read —
 * never trust the stored doc to be coherent." A doc holding
 * `{edit: true, view: false}` would otherwise hide a page the role can still
 * write to, which reads as a bug and is really an incoherent grant.
 */
export const withImpliedView = (verbs: VerbSet): VerbSet =>
  WRITE_VERBS.some((v) => verbs[v]) ? { ...verbs, view: true } : verbs;

/**
 * Effective permissions for a member = the UNION of every role they hold.
 *
 * Verbs OR together: holding two roles can only ever add capability, never
 * remove it. Roles the catalogue does not contain are skipped rather than
 * treated as empty — an unknown id means the catalogue is stale or partial,
 * and silently reading it as "grants nothing" is the confident-zero bug.
 */
export const mergeRolePermissions = (
  catalogue: RoleWithPermissions[],
  heldRoleIds: string[],
): RolePermissions => {
  const byId = new Map(catalogue.map((r) => [r.id, r]));
  const out: RolePermissions = {};
  for (const roleId of heldRoleIds) {
    const role = byId.get(roleId);
    if (!role) continue;
    for (const [schema, verbs] of Object.entries(role.permissions ?? {})) {
      if (!verbs) continue;
      const merged = { ...EMPTY_VERB_SET, ...(out[schema] ?? {}) };
      for (const v of VERBS) merged[v] = merged[v] || !!verbs[v];
      out[schema] = merged;
    }
  }
  return normalizePermissions(out);
};

/** Apply `withImpliedView` across a whole permissions object. */
export const normalizePermissions = (
  permissions: RolePermissions,
): RolePermissions => {
  const out: RolePermissions = {};
  for (const [schema, verbs] of Object.entries(permissions)) {
    if (!verbs) continue;
    const full = withImpliedView({ ...EMPTY_VERB_SET, ...verbs });
    // Omit-never-zero: drop schemas that grant nothing rather than storing
    // an all-false row, matching what the backend emits.
    if (VERBS.some((v) => full[v])) out[schema] = full;
  }
  return out;
};
